import { useState, useEffect, useCallback } from 'react';
/**
 * Hook for capturing geolocation for kiosk punch verification
 *
 * Features:
 * - Checks browser support
 * - Requests permission on demand (not automatically)
 * - Returns coordinates with accuracy
 * - Handles errors gracefully
 *
 * Usage:
 * ```tsx
 * const { location, error, isLoading, requestLocation } = useGeolocation();
 *
 * const handlePunch = async () => {
 *   const coords = await requestLocation();
 *   if (coords) {
 *     // Include coords in punch request
 *   }
 * };
 * ```
 */
export function useGeolocation() {
    const [location, setLocation] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [permissionState, setPermissionState] = useState(null);
    const isSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
    // Check permission state on mount
    useEffect(() => {
        if (!isSupported)
            return;
        // Check current permission state (if Permissions API is available)
        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                setPermissionState(result.state);
                // Listen for permission changes
                result.onchange = () => {
                    setPermissionState(result.state);
                };
            }).catch(() => {
                // Permissions API not fully supported, that's ok
            });
        }
    }, [isSupported]);
    const requestLocation = useCallback(async () => {
        if (!isSupported) {
            setError('Geolocation is not supported by this browser');
            return null;
        }
        setIsLoading(true);
        setError(null);
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition((position) => {
                const locationData = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp,
                };
                setLocation(locationData);
                setIsLoading(false);
                setPermissionState('granted');
                resolve(locationData);
            }, (err) => {
                let errorMessage;
                switch (err.code) {
                    case err.PERMISSION_DENIED:
                        errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
                        setPermissionState('denied');
                        break;
                    case err.POSITION_UNAVAILABLE:
                        errorMessage = 'Location information is unavailable.';
                        break;
                    case err.TIMEOUT:
                        errorMessage = 'Location request timed out. Please try again.';
                        break;
                    default:
                        errorMessage = 'An unknown error occurred while getting location.';
                }
                setError(errorMessage);
                setIsLoading(false);
                resolve(null);
            }, {
                enableHighAccuracy: true, // Request GPS-level accuracy
                timeout: 10000, // 10 second timeout
                maximumAge: 60000, // Accept cached position up to 1 minute old
            });
        });
    }, [isSupported]);
    return {
        location,
        error,
        isLoading,
        isSupported,
        permissionState,
        requestLocation,
    };
}
/**
 * Format coordinates for display
 */
export function formatCoordinates(lat, lng) {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(6)}° ${latDir}, ${Math.abs(lng).toFixed(6)}° ${lngDir}`;
}
/**
 * Get Google Maps URL for coordinates
 */
export function getGoogleMapsUrl(lat, lng) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
}
/**
 * Format accuracy in human-readable form
 */
export function formatAccuracy(meters) {
    if (meters < 10)
        return 'Very precise (GPS)';
    if (meters < 50)
        return 'Precise';
    if (meters < 100)
        return 'Approximate';
    if (meters < 500)
        return 'Rough estimate';
    return 'Low accuracy';
}
